import { useNavigate } from 'react-router-dom';
import type { AlertAudit, OpenFlag } from '../../api/types.js';
import { auditProvenance } from '../Breakdown/provenance.js';
import { ProvenanceFootnote } from '../Breakdown/Breakdown.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { figureExact, formatRunDate, share } from '../Audit/shared.js';
import { color, fontSize, radius } from '../../theme/tokens.js';

/**
 * One open flag (blueprint line 1194): a red chip naming the audit and its rule (FR-AL16).
 *
 * The hover is where a reader checks the claim: both figures under the SERVICE's own labels, the
 * rule, since when (and "or earlier" where history ran out), which run it comes from, and the source.
 * Activating it opens that audit's run on the Audit page (FR-AL17) — where the same recruiter reads
 * `Flagged`, by construction (SC-AL02).
 *
 * ⚠ The chip sits inside a row that is itself a button (to the recruiter's Delivery page). The
 * WRAPPER stops click and Enter/Space from reaching the row — only those, so Escape still closes the
 * panel — the same shield the export controls use (CLAUDE.md, "the row IS a button").
 */
export function ViolationChip({ flag, audit }: { flag: OpenFlag; audit: AlertAudit | undefined }) {
  const navigate = useNavigate();
  const label = audit?.alertLabel ?? audit?.name ?? flag.auditId;
  const uncatalogued = audit?.uncatalogued ?? !audit?.alertLabel;
  const since = `${formatRunDate(flag.since)}${flag.sinceIsLowerBound ? ' or earlier' : ''}`;
  const focus =
    flag.focusShare !== null
      ? `${figureExact(flag.focusCount)} of ${figureExact(flag.base)} (${share(flag.focusShare)})`
      : figureExact(flag.focusCount);
  const run =
    `From the ${formatRunDate(flag.runDate)} run` +
    (audit?.laggingMissed ? ` — this audit has not run for ${formatRunDate(audit.laggingMissed)}` : '');
  const rule = audit?.description ?? 'An audit this page does not describe yet; figures use the service’s labels.';

  return (
    <span
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
      style={{ display: 'inline-flex' }}
    >
      <Disclosure
        accessibleName={`${label}: ${flag.labels.focus} ${focus}. Flagged since ${since}. Opens the audit run.`}
        onClick={() => navigate(`/audit/${encodeURIComponent(flag.auditId)}/${flag.runDate}`)}
        panel={
          <span>
            <strong>{uncatalogued ? (audit?.name ?? flag.auditId) : label}</strong>
            <span style={{ display: 'block' }}>
              {flag.labels.base}: {figureExact(flag.base)}
            </span>
            <span style={{ display: 'block' }}>
              {flag.labels.focus}: {focus}
            </span>
            <span style={{ display: 'block', marginTop: 4 }}>Flagged since {since}</span>
            <span style={{ display: 'block' }}>{run}</span>
            {/* The rule sentence rides the source footnote, as on the Audit page — not twice. */}
            <ProvenanceFootnote provenance={auditProvenance(rule)} />
          </span>
        }
        style={{
          fontSize: fontSize.caption,
          fontWeight: 500,
          color: color.negative,
          background: color.dangerBg,
          border: `1px solid ${color.dangerBorder}`,
          padding: '3px 9px',
          borderRadius: radius.pill,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Disclosure>
    </span>
  );
}
